// Phase 56.5: 500 / uncaught-error surface.
//
// Rendered by the top-level ErrorBoundary in App.tsx when a child
// component throws an uncaught error. The boundary logs the error
// to the console and renders this page so the user sees a branded
// fallback instead of a blank white screen.
//
// Two CTAs per audit:
//   1. Retry  (window.location.reload — typical recovery for
//              transient hydration / fetch failures)
//   2. Contact support  (mailto link with the error message
//              pre-filled in the body so support has context)
//
// HANDOFF rule: no em-dashes, no regular dashes in user-facing
// copy. The error message itself (if shown) might contain dashes
// from a stack trace, but we only show a one-liner snippet so the
// risk is low; we strip them defensively before rendering.

import Logo from '../components/ui/Logo'

interface ServerErrorProps {
  errorMessage?: string
  onRetry?: () => void
}

export function ServerError({ errorMessage, onRetry }: ServerErrorProps) {
  function handleRetry() {
    if (onRetry) {
      onRetry()
    } else {
      window.location.reload()
    }
  }

  // Defensive: strip dashes from any incoming error message before
  // rendering in user-facing copy (per HANDOFF rule). Hyphens in
  // identifiers like "fetch-error" become spaces.
  const displayMessage = errorMessage ? errorMessage.replace(/[—–-]/g, ' ').slice(0, 200) : null

  const mailtoBody = errorMessage
    ? encodeURIComponent(
        `Ghost CRM hit an error:\n\n${errorMessage}\n\nWhen: ${new Date().toISOString()}`,
      )
    : encodeURIComponent('Ghost CRM hit an unexpected error and I need help recovering.')

  return (
    <main className="min-h-screen bg-muted/50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md text-center">
        <div className="mb-8 flex justify-center">
          <Logo size="lg" showWordmark={true} />
        </div>

        <p className="text-xs uppercase tracking-wider font-semibold text-warning">
          Something went wrong
        </p>
        <h1 className="mt-2 text-2xl sm:text-[32px] font-bold tracking-tight text-foreground">
          We are working on it
        </h1>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          An unexpected error happened on our end. The team has been notified. You can try again or
          reach out to support if the problem keeps happening.
        </p>

        {displayMessage && (
          <p className="mt-4 text-xs text-muted-foreground bg-card border border-border rounded-md px-3 py-2 inline-block max-w-full overflow-hidden text-ellipsis font-mono">
            {displayMessage}
          </p>
        )}

        <div className="mt-7 flex flex-col sm:flex-row items-stretch justify-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold text-white bg-primary hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href={`mailto:support@ghostcrm.io?subject=Ghost%20error&body=${mailtoBody}`}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold text-foreground bg-card border border-border hover:bg-muted"
          >
            Contact support
          </a>
        </div>

        <p className="mt-8 text-xs text-subtle">
          Need help? Email{' '}
          <a
            href="mailto:support@ghostcrm.io"
            className="text-muted-foreground hover:text-foreground underline"
          >
            support@ghostcrm.io
          </a>
        </p>
      </div>
    </main>
  )
}
